import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type SyntheticEvent } from 'react';

import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Drawer } from '../../components/Drawer';
import { Field, inputClass } from '../../components/Field';
import { FormProblem } from '../../components/FormProblem';
import { useToast } from '../../components/Toast';
import { timeZones } from '../../lib/time';
import { useAuth } from '../auth/AuthProvider';
import {
  clientsKey,
  createSite,
  deleteSite,
  devicesKey,
  listClients,
  situationLabels,
  sitesKey,
  updateSite,
  type Site,
  type SiteBody,
  type Situation,
} from './api';
import { noProblem, toProblem, type Problem } from './problem';

interface Props {
  /** The account to edit; absent to create one. */
  site?: Site;
  /** Client of a new account. */
  clientId?: string;
  canManage: boolean;
  onClose: () => void;
}

/** An account (monitored place) of a client — stored as a site (ADR-026). */
export function SiteDrawer({ site, clientId, canManage, onClose }: Props) {
  const { request, session } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const zones = useMemo(() => timeZones(), []);
  const [client, setClient] = useState(site?.client.id ?? clientId ?? '');
  const [name, setName] = useState(site?.name ?? '');
  const [address, setAddress] = useState(site?.address ?? '');
  const [zone, setZone] = useState(site?.timezone ?? '');
  const [status, setStatus] = useState<Situation>(site?.status ?? 'active');
  const [problem, setProblem] = useState<Problem>(noProblem);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const clients = useQuery({
    queryKey: [...clientsKey, 'all'],
    queryFn: async () => (await listClients(request, { page: 1, size: 100 })).items,
  });

  const invalidate = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: sitesKey }),
      qc.invalidateQueries({ queryKey: clientsKey }),
      qc.invalidateQueries({ queryKey: devicesKey }),
    ]);
  };

  const save = useMutation({
    mutationFn: () => {
      if (!site)
        return createSite(request, {
          client_id: client,
          name,
          address,
          timezone: zone,
          status,
        });
      const body: SiteBody = {};
      if (client !== site.client.id) body.client_id = client;
      if (name.trim() !== site.name) body.name = name;
      if (address.trim() !== site.address) body.address = address;
      if (zone !== (site.timezone ?? '')) body.timezone = zone;
      if (status !== site.status) body.status = status;
      return updateSite(request, site.id, body);
    },
    onSuccess: async (s) => {
      await invalidate();
      notify(site ? 'Conta atualizada.' : `Conta ${s.name} criada.`);
      onClose();
    },
    onError: (err) => {
      setProblem(toProblem(err));
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteSite(request, site?.id ?? ''),
    onSuccess: async () => {
      await invalidate();
      notify('Conta excluída.');
      onClose();
    },
    onError: (err) => {
      setConfirmDelete(false);
      setProblem(toProblem(err));
    },
  });

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    setProblem(noProblem);
    if (!client) {
      setProblem({ fields: { client_id: 'Selecione o cliente.' }, form: null });
      return;
    }
    save.mutate();
  };

  const readOnly = !canManage;
  return (
    <Drawer
      title={site ? site.name : 'Nova conta'}
      description={
        site
          ? `${site.client.name} · ${String(site.device_count)} dispositivo(s)`
          : 'Um local monitorado do cliente'
      }
      onClose={onClose}
      footer={
        <>
          {site && canManage && (
            <Button
              className="mr-auto text-crit!"
              onClick={() => {
                setConfirmDelete(true);
              }}
            >
              Excluir
            </Button>
          )}
          <Button onClick={onClose}>{readOnly ? 'Fechar' : 'Cancelar'}</Button>
          {canManage && (
            <Button variant="primary" type="submit" form="site-form" disabled={save.isPending}>
              {save.isPending ? 'Salvando…' : site ? 'Salvar alterações' : 'Criar conta'}
            </Button>
          )}
        </>
      }
    >
      <form id="site-form" onSubmit={submit} noValidate className="space-y-4">
        <FormProblem message={problem.form} />
        <Field
          label="Cliente"
          {...(site ? { hint: 'Trocar o cliente move a conta e os seus dispositivos.' } : {})}
          error={problem.fields.client_id}
        >
          {({ id, describedBy, invalid }) => (
            <select
              id={id}
              disabled={readOnly || clients.isPending}
              value={client}
              onChange={(e) => {
                setClient(e.target.value);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className={inputClass}
            >
              <option value="">{clients.isPending ? 'Carregando…' : 'Selecione…'}</option>
              {clients.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field
          label="Nome da conta"
          hint="Ex.: Loja do Centro. Único dentro do cliente."
          error={problem.fields.name}
        >
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              data-autofocus
              readOnly={readOnly}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="Endereço" error={problem.fields.address}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              readOnly={readOnly}
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className={inputClass}
            />
          )}
        </Field>
        <Field
          label="Fuso horário"
          hint="Use um fuso próprio apenas se a conta estiver em outra região."
          error={problem.fields.timezone}
        >
          {({ id, describedBy, invalid }) => (
            <select
              id={id}
              disabled={readOnly}
              value={zone}
              onChange={(e) => {
                setZone(e.target.value);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className={inputClass}
            >
              <option value="">Usar o da organização ({session?.tenant.timezone ?? 'UTC'})</option>
              {zones.map((z) => (
                <option key={z} value={z}>
                  {z.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Situação" error={problem.fields.status}>
          {({ id, describedBy, invalid }) => (
            <select
              id={id}
              disabled={readOnly}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as Situation);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className={inputClass}
            >
              {(Object.keys(situationLabels) as Situation[]).map((s) => (
                <option key={s} value={s}>
                  {situationLabels[s].replace(/o$/, 'a')}
                </option>
              ))}
            </select>
          )}
        </Field>
      </form>
      {confirmDelete && site && (
        <ConfirmDialog
          title={`Excluir ${site.name}?`}
          confirmLabel="Excluir conta"
          busy={remove.isPending}
          onCancel={() => {
            setConfirmDelete(false);
          }}
          onConfirm={() => {
            remove.mutate();
          }}
        >
          A conta só pode ser excluída quando não tiver dispositivos. Esta ação não pode ser
          desfeita.
        </ConfirmDialog>
      )}
    </Drawer>
  );
}
